import {
  cents,
  formatMoney,
  isCheckDeposit,
  planDepositSplit,
  toCents,
  type Cents,
  type DepositLink,
  type PaymentSlot,
  type SplitPart,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t, tn } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../session";
import {
  confirmDeposit,
  confirmDepositForRequest,
  dismissDeposit,
  fetchCardPayoutProblems,
  fetchMercurySuggestions,
  fetchOpenDeposits,
  fetchPaymentContexts,
  fetchSetAsideAutomatically,
  pollPaymentSources,
  splitDeposit,
  suggestJobsForDeposit,
  undoSetAside,
  type DepositSuggestion,
  type MercuryDepositRow,
} from "../../lib/payments";

/**
 * Money that has arrived, waiting to be told whose it is.
 *
 * This sits above the ongoing board because that is where the person chasing
 * money already is, and because a deposit nobody matches is money the company
 * has been paid and does not know it. It is deliberately loud when there is
 * something to do and invisible when there is not.
 *
 * **Confirming is a decision, not a formality.** Jonathon was asked how a bank
 * deposit should find its job and chose this: Roof Board suggests, a person
 * says yes. The box a confirmation ticks — "First Check" or "Final Check" —
 * takes a job off this board and moves its stage to complete, so the cost of a
 * wrong automatic match is a job the company stops chasing while it is still
 * owed money.
 *
 * ## The layout
 *
 * A card per deposit, chosen by Jonathon on 2026-09-06 from three drawn side by
 * side. It was a list of one-line rows before, and two things decided it: this
 * screen is opened on a phone as often as at a desk, and the green edge answers
 * "which of these is easy" before you read a word.
 *
 * The likely job is named on the closed card, which the old layout could not
 * do. Working out what a deposit could be cost six queries per job, so it was
 * only ever done for the row somebody opened. `fetchPaymentContexts` batches
 * the whole board into six queries once and the ranking is then arithmetic.
 *
 * Opening the board is also what asks QuickBooks and Mercury what has happened
 * since last time. Both polls are safe to repeat, and `sync` — has a request
 * we sent been paid? — runs on a schedule as well from 2026-09-12
 * ([ADR 0136](../../../../docs/adr/0136-mercury-is-asked-on-a-schedule.md)).
 *
 * ## What Mercury already knows
 *
 * A credit that settles a payment request Roof Board raised is not an unknown
 * at all: the request names the job, the payment and the amount. Where the
 * bank line carries an identifier the poll has already confirmed it and the
 * card is not here. Where it does not — sending banks drop memos — the card
 * carries one sentence saying whose invoice this is, and one button
 * ([ADR 0142](../../../../docs/adr/0142-a-bank-line-finds-its-payment-request.md)).
 * Never a match on the amount or the name by itself.
 */
export function DepositsPanel() {
  const { profileId } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Asked once, when the board is opened. Deliberately NOT part of the list's
  // own loader: confirming a deposit reloads the list, and re-asking QuickBooks
  // and Mercury at that moment would both waste two round trips and overwrite
  // the "ticked First Check" line the person just earned.
  const poll = useAsync(() => pollPaymentSources(), []);
  const polled = poll.data;
  const ready = polled === null ? "waiting" : "polled";

  const deposits = useAsync(() => fetchOpenDeposits(), [ready]);
  const contexts = useAsync(() => fetchPaymentContexts(), [ready]);
  // What Mercury's own paid requests say about these lines. Separate from the
  // amount-ranking above because it is different evidence: a payment request
  // knows the job, where an amount only resembles one.
  const mercury = useAsync(
    async () => fetchMercurySuggestions(await fetchOpenDeposits()),
    [ready],
  );

  // Why card money has not been taken apart: a refund inside the payout, a
  // credit that has not landed, a customer nothing could name. Nothing read
  // these until 2026-09-13, so a payout simply sat there (ADR 0138).
  const cardProblems = useAsync(() => fetchCardPayoutProblems(), [ready]);

  // What the software decided by itself, kept where it can be checked. A list
  // that quietly shortens itself is a list people stop trusting.
  const [showSetAside, setShowSetAside] = useState(false);
  const setAside = useAsync(
    () => (showSetAside ? fetchSetAsideAutomatically() : Promise.resolve([])),
    [showSetAside, ready],
  );

  const rows = deposits.data ?? [];
  const applied = polled?.applied ?? 0;

  // Everything the poll did without being asked, in one sentence, so nothing
  // this screen decided is invisible. What just happened beats it.
  const didItself = [
    applied > 0
      ? tn(
          applied,
          "1 payment from QuickBooks ticked their submitted box",
          "{n} payments from QuickBooks ticked their submitted box",
        )
      : null,
    (polled?.closed ?? 0) > 0
      ? tn(
          polled!.closed,
          "1 matched to the job QuickBooks had already put it against",
          "{n} matched to the job QuickBooks had already put them against",
        )
      : null,
    (polled?.linked ?? 0) > 0
      ? t("{n} matched to the payment request the customer paid", {
          n: polled!.linked,
        })
      : null,
    (polled?.cards ?? 0) > 0
      ? tn(
          polled!.cards,
          "1 card payment taken out of a Stripe payout",
          "{n} card payments taken out of a Stripe payout",
        )
      : null,
    (polled?.setAside ?? 0) > 0
      ? t("{n} set aside as not a customer payment", { n: polled!.setAside })
      : null,
  ].filter(Boolean);
  const message =
    note ??
    (didItself.length > 0
      ? `${didItself.join(", ")}${didItself.length > 1 ? "" : "."}`
      : null);
  const problem =
    error ?? (polled?.problems.length ? polled.problems.join(" · ") : null);

  const setAsideRows = setAside.data ?? [];
  const waiting = rows.reduce((sum, row) => sum + Number(row.amount_cents), 0);

  if (poll.loading || (deposits.loading && !deposits.data)) return null;
  if (rows.length === 0 && !message && !problem && !showSetAside) return null;

  const reload = async () => {
    await Promise.all([deposits.reload(), setAside.reload()]);
  };

  const putBack = async (
    id: string,
    counterparty: Parameters<typeof undoSetAside>[1],
  ) => {
    setBusy(true);
    try {
      await undoSetAside(id, counterparty);
      setNote(t("Put back. It is in the list above, waiting to be matched."));
      setError(null);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="block mi">
      <div className="mi-head">
        <h2>{t("Money in")}</h2>
        {rows.length > 0 && (
          <span className="mi-waiting">
            <b>{formatMoney(waiting as Cents)}</b> {t("waiting")}
          </span>
        )}
      </div>

      <ErrorNote error={problem} />
      {(cardProblems.data ?? []).length > 0 && (
        <div className="na-hint" role="status">
          {t("Card payouts waiting: {list}", {
            list: (cardProblems.data ?? []).join(" · "),
          })}
        </div>
      )}
      {message && (
        <div className="na-hint" role="status">
          {message}
        </div>
      )}

      {rows.length === 0 ? (
        <div className="na-hint">{t("Nothing waiting to be matched.")}</div>
      ) : (
        <div className="na-hint">
          {t(
            "These landed in the Mercury account. Say which job each one belongs to and Roof Board ticks that job’s payment box — it will not guess.",
          )}
        </div>
      )}

      <div className="mi-cards">
        {rows.map((deposit) => (
          <DepositCard
            key={deposit.id}
            deposit={deposit}
            suggestions={
              contexts.data
                ? suggestJobsForDeposit(deposit, contexts.data)
                : null
            }
            mercuryLink={mercury.data?.get(deposit.id) ?? null}
            open={open === deposit.id}
            busy={busy}
            onToggle={() => setOpen(open === deposit.id ? null : deposit.id)}
            onConfirmRequest={async () => {
              const link = mercury.data?.get(deposit.id);
              if (!link || link.kind === "none") return;
              setBusy(true);
              try {
                const { ticked, why } = await confirmDepositForRequest(
                  deposit,
                  link.request,
                  profileId,
                );
                setNote(
                  ticked.length > 0
                    ? t("Ticked {boxes}. {why}", {
                        boxes: tickedList(ticked),
                        why,
                      })
                    : t(
                        "Recorded against the job. {why} No payment box was ticked.",
                        {
                          why,
                        },
                      ),
                );
                setError(null);
                setOpen(null);
                await reload();
                await mercury.reload();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
            onConfirm={async (jobId, slot, receipt) => {
              setBusy(true);
              try {
                const { ticked, why } = await confirmDeposit(
                  deposit,
                  jobId,
                  profileId,
                  "person",
                  { slot, receipt },
                );
                setNote(
                  (ticked.length > 0
                    ? t("Ticked {boxes}. {why}", {
                        boxes: tickedList(ticked),
                        why,
                      })
                    : t(
                        "Recorded against the job. {why} No payment box was ticked.",
                        {
                          why,
                        },
                      )) +
                    (receipt === false
                      ? ` ${t("The receipt is filed on the job, and nobody is being asked to send it.")}`
                      : ""),
                );
                setError(null);
                setOpen(null);
                await reload();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
            onSplit={async (parts) => {
              setBusy(true);
              try {
                const plan = planDepositSplit(
                  cents(deposit.amount_cents),
                  parts,
                );
                const { recorded, failed } = await splitDeposit(
                  deposit,
                  plan,
                  profileId,
                );
                setNote(
                  t("Split into {n} payments. {recorded} recorded", {
                    n: plan.lines.length,
                    recorded,
                  }) +
                    (failed.length > 0
                      ? tn(
                          failed.length,
                          "; 1 could not be and is back on this list: {list}",
                          "; {n} could not be and are back on this list: {list}",
                          {
                            list: failed
                              .map((f) => `${f.label} — ${f.why}`)
                              .join(" · "),
                          },
                        )
                      : "."),
                );
                setError(null);
                setOpen(null);
                await reload();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
            onDismiss={async () => {
              const reason = window.prompt(
                t("Set this deposit aside. What is it?"),
                "Not a customer payment",
              );
              if (reason === null) return;
              setBusy(true);
              try {
                const { notice } = await dismissDeposit(
                  deposit,
                  reason,
                  profileId,
                );
                // Said out loud on the one that changes how the screen behaves.
                // Software that starts doing something new without mentioning
                // it is software people stop believing.
                setNote(notice);
                setError(null);
                await reload();
              } catch (e) {
                setError(e instanceof Error ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          />
        ))}
      </div>

      {/* Everything the software set aside on its own, one click away and one
          click back. Nothing here is hidden — it is folded, which is the
          difference between a screen that is quiet and one that is keeping
          something from you. */}
      <button
        className="mi-more"
        onClick={() => setShowSetAside(!showSetAside)}
        disabled={busy}
      >
        {showSetAside ? "▾" : "▸"} {t("What was set aside automatically")}
      </button>

      {showSetAside &&
        (setAside.loading ? (
          <div className="na-hint">{t("Looking…")}</div>
        ) : setAsideRows.length === 0 ? (
          <div className="na-hint">
            {t(
              "Nothing has been set aside automatically. Roof Board learns a payer once somebody has set it aside twice.",
            )}
          </div>
        ) : (
          setAsideRows.map((deposit) => (
            <div className="mi-row" key={deposit.id}>
              <div>
                <div className="cn">
                  {formatMoney(deposit.amount_cents as Cents)}
                  {deposit.counterparty ? ` · ${deposit.counterparty}` : ""}
                </div>
                <div className="na-hint">{deposit.dismissed_reason}</div>
              </div>
              <button
                className="btn-ghost"
                disabled={busy}
                // Fire and forget: putBack() shows its own errors.
                onClick={() => void putBack(deposit.id, deposit.counterparty)}
              >
                {t("Put it back")}
              </button>
            </div>
          ))
        ))}
    </div>
  );
}

/** "First Check and Final Check": the boxes a confirmation ticked, in words. */
function tickedList(ticked: readonly string[]): string {
  return ticked.map((box) => t(box)).join(` ${t("and")} `);
}

/**
 * One deposit, and what it probably is.
 *
 * Closed, it says the amount, who sent it and the job it fits — which is
 * usually the whole answer. Opened, it offers every signed customer and a
 * search, for the times it is not.
 */
function DepositCard({
  deposit,
  suggestions,
  mercuryLink,
  open,
  busy,
  onToggle,
  onConfirm,
  onConfirmRequest,
  onSplit,
  onDismiss,
}: {
  deposit: MercuryDepositRow;
  suggestions: DepositSuggestion[] | null;
  /** What Mercury's own paid requests say this line is (ADR 0142). */
  mercuryLink: DepositLink | null;
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onConfirm: (
    jobId: string,
    slot?: PaymentSlot | null,
    receipt?: boolean,
  ) => Promise<void>;
  onConfirmRequest: () => Promise<void>;
  onSplit: (parts: SplitPart[]) => Promise<void>;
  onDismiss: () => Promise<void>;
}) {
  const [query, setQuery] = useState("");
  const [splitting, setSplitting] = useState(false);

  /*
   * Whether this customer is to hear about it at all.
   *
   * Confirming a check deposit is the one thing this screen does that leaves
   * the building (ADR 0125), and it did it silently until 2026-09-14 — job
   * 1023's customer had the receipt 0.7 seconds after Jonathon matched the
   * money, with nothing having asked.
   *
   * ADR 0140 answers most of that: the receipt now waits to be read and sent
   * rather than going by itself. What it cannot answer is a check that should
   * never be receipted — one the customer already has a receipt for — because
   * "Don't send" on the prompt deliberately leaves it waiting on the list. So
   * the tick is here, on the screen where somebody is looking at the money,
   * and unticking it files the receipt on the job and asks nobody to send it.
   *
   * On a check and nowhere else: a card payout emails no one, so its card
   * carries no opinion about a receipt it was never going to send, and the
   * flag stays absent rather than false on the way to `confirmDeposit`.
   */
  const isCheck = isCheckDeposit({ description: deposit.description });
  const [receipt, setReceipt] = useState(true);
  const sendReceipt = isCheck ? receipt : undefined;

  const all = suggestions ?? [];
  // The green edge and the one-click confirm both rest on this: the closest job
  // is one the amount actually settles, net of a card fee.
  const fits = all[0]?.match.matched ? all[0] : null;

  /*
   * Mercury's own answer, which beats the amount whenever it exists.
   *
   * A paid payment request knows the job, the payment and what was paid. An
   * amount that fits a job only resembles one — and on the ordinary 50/50
   * schedule the first payment and the final payment are the same number, so
   * "fits" regularly cannot say which of the two it is. When the request is
   * there it is offered instead, with the sentence saying why.
   */
  /*
   * Mercury's own answer, and how far it goes.
   *
   * `suggested` is one click: exactly one paid request fits this credit to the
   * cent. `hint` is a name and no button — the line says whose payment it
   * probably is, and the amount does not match, so confirming it in one click
   * would tick a whole payment as cleared and book the difference as a card
   * fee. That one goes to split or Find the job (ADR 0142).
   */
  const says = mercuryLink && mercuryLink.kind !== "none" ? mercuryLink : null;
  const oneClick = says?.kind === "suggested" ? says : null;

  const needle = query.trim().toLowerCase();
  const shown = needle
    ? all.filter((s) => s.search.toLowerCase().includes(needle)).slice(0, 25)
    : all.slice(0, 6);

  const when = deposit.posted_at
    ? deposit.posted_at.slice(0, 10)
    : t("pending");
  const line =
    [deposit.counterparty, deposit.description].filter(Boolean).join(" · ") ||
    t("No description on the bank line.");

  return (
    <div className={`mi-card${oneClick || (fits && !says) ? " fits" : ""}`}>
      <div className="mi-top">
        <span className="mi-amt">
          {formatMoney(deposit.amount_cents as Cents)}
        </span>
        {(suggestions || says) && (
          <span className={`mi-pill${oneClick || fits ? " ok" : ""}`}>
            {/* "Mercury knows this one" was said over an amount-only guess
                until 2026-09-13, which overstated what anybody knew. A
                suggestion says it looks like a payment; only a line carrying
                Mercury's own id is ever filed without being read. */}
            {oneClick
              ? t("Looks like a payment request")
              : says
                ? t("Probably this customer")
                : fits
                  ? t("Matches a payment")
                  : t("Needs a person")}
          </span>
        )}
      </div>

      <div className="mi-from">{line}</div>
      <div className="na-hint">{when}</div>

      {says && !open && <div className="mi-says">{says.why}</div>}
      {!says && fits && !open && <div className="mi-job">{fits.label}</div>}

      {isCheck && (
        <label className="check-row mi-receipt">
          <input
            type="checkbox"
            checked={receipt}
            disabled={busy}
            onChange={(event) => setReceipt(event.target.checked)}
          />{" "}
          {t("Send this customer a receipt")}
        </label>
      )}

      <div className="mi-acts">
        {oneClick && !open ? (
          <button
            className="mi-go"
            disabled={busy}
            onClick={() => void onConfirmRequest()}
          >
            {t("Yes, that payment")}
          </button>
        ) : fits && !open && !says ? (
          <button
            className="mi-go"
            disabled={busy}
            onClick={() => void onConfirm(fits.jobId, null, sendReceipt)}
          >
            {t("Yes, that job")}
          </button>
        ) : (
          <button className="mi-go" disabled={busy} onClick={onToggle}>
            {open ? t("Close") : t("Find the job")}
          </button>
        )}
        {/* Beside a one-click confirm, whichever kind it is. A hint already
            shows "Find the job" as its main button, so a second one saying
            the same thing would only be noise. */}
        {(oneClick || (fits && !says)) && !open && (
          <button className="btn-ghost" disabled={busy} onClick={onToggle}>
            {t("A different job")}
          </button>
        )}
        {/* Stripe pays out in sweeps, so a single bank line is regularly two
            customers or five. Offered on every deposit rather than only the
            ones that match nothing: a batched payout can land on exactly one
            job's number by coincidence, and that is the expensive mistake. */}
        <button
          className="btn-ghost"
          disabled={busy}
          onClick={() => setSplitting(!splitting)}
        >
          {splitting ? t("Not a split") : t("More than one payment")}
        </button>
        <button
          className="btn-ghost"
          disabled={busy}
          onClick={() => void onDismiss()}
        >
          {t("Not a customer")}
        </button>
      </div>

      {splitting && (
        <SplitPanel
          deposit={deposit}
          suggestions={suggestions}
          busy={busy}
          onCancel={() => setSplitting(false)}
          onSplit={onSplit}
        />
      )}

      {open && (
        <div className="mi-pick">
          {!suggestions && <div className="na-hint">{t("Looking…")}</div>}
          {suggestions && all.length === 0 && (
            <div className="na-hint">
              {t(
                "No signed job is waiting for money. This may be a refund, a transfer, or a job that has not been signed in Roof Board yet.",
              )}
            </div>
          )}

          {/* Every signed customer is on this list, so it needs a way through
              it. The ranked few are enough on a normal day; the box is for the
              day they are not. */}
          {all.length > 0 && (
            <input
              className="mi-search"
              type="search"
              placeholder={t("Search a customer, job number or address…")}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              disabled={busy}
            />
          )}

          {shown.map((suggestion) => (
            <SuggestionRow
              key={suggestion.jobId}
              suggestion={suggestion}
              busy={busy}
              onConfirm={(slot) =>
                void onConfirm(suggestion.jobId, slot, sendReceipt)
              }
            />
          ))}

          {needle && shown.length === 0 && (
            <div className="na-hint">
              {t(
                "Nobody signed matches that. A job that is paid in full is not on this list.",
              )}
            </div>
          )}

          {!needle && all.length > shown.length && (
            <div className="na-hint">
              {tn(
                all.length - shown.length,
                "1 more signed customer — search above to find them.",
                "{n} more signed customers — search above to find them.",
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Cents as a person would type them, for filling the amount box in.
 *
 * `formatMoney` is for reading — it carries a dollar sign and thousands
 * separators — and putting that in an input somebody then edits gives them a
 * string to fight with. `toCents` would parse it back either way; this is
 * about what the box looks like while they are in it.
 */
function toDollars(amount: Cents): string {
  return (amount / 100).toFixed(2);
}

/**
 * A batched payout, taken apart.
 *
 * Jonathon, 2026-09-08, looking at a $1,588.79 Stripe line in Money In that was
 * really Tyler Christiansen's $1,436.86 and Jim Messer's $200.00: it needs "the
 * option to split and choose between customers as well as if it's the first or
 * last payment".
 *
 * ## What a person types
 *
 * **The gross.** The figure beside the customer's name on the Stripe payout —
 * what the customer paid, before Stripe took anything. Not the amount that
 * reached the bank, which is what a person would otherwise have to work out
 * themselves for every line, with a calculator, from a percentage. Roof Board
 * does that half: it shows the fee it took out and what was left, and counts
 * the deposit down to zero as the parts are filled in.
 *
 * **Take the rest** exists for the last line, where typing the gross and hoping
 * the rounding lands exactly on zero is a worse experience than being handed
 * the remainder. It works backwards to the charge that would have left that
 * much, which on the reference payout lands on Jim Messer's $200.00 exactly.
 *
 * ## Why the payment is a choice and not a deduction
 *
 * On the default 50/50 schedule the first payment and the final payment are
 * the same number, so an amount cannot say which it is. An unsplit deposit gets
 * around that by asking whether First Check is already ticked. Inside a split
 * that is not enough — two parts can land on one job's schedule in one payout —
 * so the person says, and `confirmDeposit` ticks what they said rather than
 * what it worked out. "Work it out" is still the default, and is right whenever
 * the amounts are actually distinguishable.
 */
function SplitPanel({
  deposit,
  suggestions,
  busy,
  onCancel,
  onSplit,
}: {
  deposit: MercuryDepositRow;
  suggestions: DepositSuggestion[] | null;
  busy: boolean;
  onCancel: () => void;
  onSplit: (parts: SplitPart[]) => Promise<void>;
}) {
  // Typed dollars, kept as the string the person typed. Parsing on every
  // keystroke and writing the number back is what makes a money field delete
  // the decimal point out from under somebody mid-type.
  const [rows, setRows] = useState<
    { jobId: string; slot: string; typed: string; rest: boolean }[]
  >([
    { jobId: "", slot: "", typed: "", rest: false },
    { jobId: "", slot: "", typed: "", rest: true },
  ]);

  // What each job is actually sold on. A job split in halves offers two
  // payments; one sold in draws offers four; and a payment nobody typed is not
  // on the list at all, because choosing it would mean nothing.
  const paymentsFor = (jobId: string) =>
    (suggestions ?? []).find((s) => s.jobId === jobId)?.payments ?? [];

  const parts: SplitPart[] = rows.map((row) => ({
    jobId: row.jobId || null,
    slot: row.slot ? (Number(row.slot) as SplitPart["slot"]) : null,
    grossCents: row.rest ? null : toCents(row.typed),
    takesTheRest: row.rest,
  }));
  const plan = planDepositSplit(cents(deposit.amount_cents), parts);

  const patch = (index: number, change: Partial<(typeof rows)[number]>) =>
    setRows(rows.map((row, i) => (i === index ? { ...row, ...change } : row)));

  return (
    <div className="mi-split">
      <div className="na-hint">
        {t(
          "Stripe pays out in batches, so one bank line can be several customers. Type what each customer paid — the figure beside their name on the Stripe payout, before Stripe’s fee. Roof Board takes the fee off and counts the {amount} down to nothing.",
          { amount: formatMoney(cents(deposit.amount_cents)) },
        )}
      </div>

      {rows.map((row, index) => {
        const line = plan.lines[index];
        return (
          <div className="mi-part" key={index}>
            <select
              value={row.jobId}
              disabled={busy}
              onChange={(e) => {
                const jobId = e.target.value;
                // A payment chosen on the previous job may not exist on this
                // one — a job sold in halves has no third payment — and a
                // stale choice would be recorded against a slot the customer
                // was never asked for.
                const keeps = paymentsFor(jobId).some(
                  (payment) => String(payment.slot) === row.slot,
                );
                patch(index, { jobId, slot: keeps ? row.slot : "" });
              }}
            >
              <option value="">{t("Which customer?")}</option>
              {(suggestions ?? []).map((suggestion) => (
                <option key={suggestion.jobId} value={suggestion.jobId}>
                  {suggestion.label}
                </option>
              ))}
            </select>

            <select
              value={row.slot}
              disabled={busy || !row.jobId}
              title={
                row.jobId
                  ? t("Which payment this settles")
                  : t("Choose the customer first — the payments are theirs.")
              }
              onChange={(e) => {
                const slot = e.target.value;
                // Choosing a payment fills in what that payment is worth, so
                // the ordinary case — a customer paying exactly what they were
                // asked for — needs no typing at all. Only when it is blank:
                // an amount somebody has already typed is theirs.
                const owed = paymentsFor(row.jobId).find(
                  (payment) => String(payment.slot) === slot,
                );
                patch(index, {
                  slot,
                  typed:
                    !row.rest && !row.typed.trim() && owed
                      ? toDollars(owed.amount)
                      : row.typed,
                });
              }}
            >
              <option value="">{t("Work it out")}</option>
              {paymentsFor(row.jobId).map((payment) => (
                <option key={payment.slot} value={String(payment.slot)}>
                  {t(payment.label)} · {formatMoney(payment.amount)}
                </option>
              ))}
            </select>

            {row.rest ? (
              <span className="mi-rest">
                {line ? formatMoney(line.grossCents) : "—"}
                <span className="na-hint"> {t("— the rest")}</span>
              </span>
            ) : (
              <input
                className="mi-amt-in"
                type="text"
                inputMode="decimal"
                placeholder={t("What they paid")}
                value={row.typed}
                disabled={busy}
                onChange={(e) => patch(index, { typed: e.target.value })}
              />
            )}

            {line && line.netCents > 0 && (
              <span className="na-hint mi-fee">
                {t("less {fee} fee = {net} in the bank", {
                  fee: formatMoney(line.feeCents),
                  net: formatMoney(line.netCents),
                })}
              </span>
            )}

            <button
              className="btn-ghost"
              disabled={busy || rows.length <= 2}
              title={
                rows.length <= 2
                  ? t("A split needs at least two payments.")
                  : t("Take this one off the split")
              }
              onClick={() => setRows(rows.filter((_, i) => i !== index))}
            >
              ✕
            </button>
          </div>
        );
      })}

      <div className="mi-split-foot">
        <button
          className="btn-ghost"
          disabled={busy}
          onClick={() =>
            // The new one is typed and the last one keeps taking the rest, so
            // the running total still lands on zero by itself.
            setRows([
              ...rows.slice(0, -1),
              { jobId: "", slot: "", typed: "", rest: false },
              ...rows.slice(-1),
            ])
          }
        >
          {t("Another customer")}
        </button>

        <span className={plan.ready ? "mi-balanced" : "na-hint"}>
          {plan.ready
            ? t("Balances to {amount}.", {
                amount: formatMoney(cents(deposit.amount_cents)),
              })
            : plan.problem}
        </span>

        <button
          className="mi-go"
          disabled={busy || !plan.ready}
          onClick={() => void onSplit(parts)}
        >
          {t("Record {n} payments", { n: plan.lines.length })}
        </button>
        <button className="btn-ghost" disabled={busy} onClick={onCancel}>
          {t("Cancel")}
        </button>
      </div>
    </div>
  );
}

/**
 * One job this deposit might be.
 *
 * A job whose amounts do not line up is still listed, with the reason — the
 * useful thing on a screen like this is usually "here is the job you meant and
 * here is why the number is wrong", not a shorter list.
 */
function SuggestionRow({
  suggestion,
  busy,
  onConfirm,
}: {
  suggestion: DepositSuggestion;
  busy: boolean;
  onConfirm: (slot?: PaymentSlot | null) => void;
}) {
  const exact = suggestion.match.matched;
  // A draw against work in progress. It has no checklist box — the board never
  // waits on one — but it is a payment this customer was asked for, and saying
  // "Record as the second payment" is a great deal more than the shrugging
  // "Record it anyway" this row could offer before.
  const draw = !exact ? suggestion.middleFit : null;
  return (
    <div className="mi-row">
      <div>
        <div className="cn">{suggestion.label}</div>
        <div className="na-hint">
          {draw
            ? t(
                "{label} on this job. No box on the checklist covers the middle payments, so none is ticked.",
                { label: t(draw.label) },
              )
            : suggestion.match.why}
        </div>
      </div>
      {/* Choosable either way. Whose money it is, is a person's to say; which
          payment box it ticks is arithmetic, and an amount that settles none of
          them ticks none. The button says which is about to happen. */}
      <button
        className={exact || draw ? "mi-go" : "btn-ghost"}
        disabled={busy}
        onClick={() => onConfirm(draw?.slot ?? null)}
        title={
          exact
            ? t("Tick this job's payment box")
            : draw
              ? t(
                  "Record it against this job as the {label}. The middle payments have no box, so none is ticked.",
                  { label: t(draw.label).toLowerCase() },
                )
              : t(
                  "Record it against this job. The amount fits no scheduled payment, so no box is ticked.",
                )
        }
      >
        {exact
          ? t("This one")
          : draw
            ? t("Record as the {label}", {
                label: t(draw.label).toLowerCase(),
              })
            : t("Record it anyway")}
      </button>
    </div>
  );
}
